import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { logEvents } from "../../utils/logging";
import { recipeCreation } from "./createRecipe.thunk";
import { createRecipeInitialState, Recipe, RecipeCreationPropsPayload, RecipeCreationRejectedEvent, recipeDataInterface, recipeSubStepInterface, Substep } from "./createRecipe.types";
import { initialRecipeData, initialSubstep } from "./createRecipe.utils";
import { RecipeState } from "../recipePage/recipePage.types";
import { setRecipeAndIngredients } from "../recipePage/recipePage.slice";
import { RootState } from "../store/store";

const initialState: createRecipeInitialState = {
  loadingRecipeCreating: false,
  initialRecipeOpen: false,
  editStepView: false,
  oldRecipeData: null,
  recipeData: initialRecipeData,
  subStepToEdit: initialSubstep,
  initialSubstep: null,
  substepIndex: 0,
  stepId: null,
  createSubstepOpen: false,
  referralReview: null,
};

export const createRecipeSlice = createSlice({
  name: "recipe creation",
  initialState,
  reducers: {
    setLoadingRecipeCreating: (state, action: PayloadAction<boolean>) => {
      state.loadingRecipeCreating = action.payload
    },
    setInitialRecipeOpen: (state, action: PayloadAction<boolean>) => {
      state.initialRecipeOpen = action.payload
    },
    setEditStepView: (state, action: PayloadAction<boolean>) => {
      state.editStepView = action.payload
    },
    setOldRecipeData: (state, action: PayloadAction<Recipe>) => {
      state.oldRecipeData = action.payload
    },
    setRecipeData: (state, action: PayloadAction<recipeDataInterface>) => {
      state.recipeData = action.payload
    },
    setSubstepToEdit: (state, action: PayloadAction<recipeSubStepInterface>) => {
      state.subStepToEdit = action.payload
    },
    setInitialSubstep: (state, action: PayloadAction<Substep | null>) => {
      state.initialSubstep = action.payload
    },
    setCreateSubstepOpen: (state, action: PayloadAction<boolean>) => {
      state.createSubstepOpen = action.payload
    },
    setReferralReview: (state, action: PayloadAction<string|null>) => {
      state.referralReview = action.payload
    },
    setEditSubstep: (state, action: PayloadAction<{ substep: Substep, substepOperation: string, substepId: string, subStepUId: string, substepIndex: number, stepId: string | null }>) => {
      state.initialSubstep = action.payload.substep;
      state.createSubstepOpen = true;
      state.stepId = action.payload.stepId;
      state.substepIndex = action.payload.substepIndex;
    }

  },
  extraReducers: (builder) => {
    builder.addCase(recipeCreation.pending, (state) => {
      state.loadingRecipeCreating = true;
    }).addCase(recipeCreation.fulfilled, (state, action: PayloadAction<{ userId: string | null, deviceId: string | null, recipeId: string }>) => {
      state.loadingRecipeCreating = false;
      const { userId, deviceId, recipeId } = action.payload;
      logEvents('null', 'APP_RECIPE_CREATED', {
        userId,
        deviceId,
        recipeId,
      })
    }).addCase(recipeCreation.rejected, (state, action: PayloadAction<RecipeCreationRejectedEvent | undefined, string, RecipeCreationPropsPayload>) => {
      state.loadingRecipeCreating = false;
      const userId = action.payload?.userId ?? 'User Id not found';
      const deviceId = action.payload?.deviceId ?? 'Device Id not found';
      logEvents('null', 'APP_RECIPE_CREATION_FAILED', {
        userId,
        deviceId,
      })
    }).addCase(setRecipeAndIngredients, (state, action: PayloadAction<{ recipe: RecipeState, newRecipedata: Recipe, ingredients: Array<any> }>) => {
      state.oldRecipeData = action.payload.newRecipedata;
    })
  }
});

export const { setLoadingRecipeCreating, setInitialRecipeOpen, setEditStepView, setRecipeData, setSubstepToEdit, setCreateSubstepOpen, setOldRecipeData, setInitialSubstep, setEditSubstep, setReferralReview } = createRecipeSlice.actions;

export const referralReviewSelector = ((state:RootState) => state.recipeCreation.referralReview);

export default createRecipeSlice.reducer;